import jsPDF from 'jspdf';

import logoUrl from '../DEEPECHO logo.png';

/**
 * A printable enrollment sheet: what someone hands to a clinician so they can register a
 * tablet without being talked through it.
 *
 * Laid out with jsPDF's own text and image calls rather than rasterising HTML. The page is
 * mostly type and one image, so drawing it directly keeps the text vector-sharp and
 * selectable, keeps the file small, and avoids depending on html2canvas reproducing the
 * app's CSS faithfully. The QR is supplied as an already-rendered canvas so what prints is
 * exactly what the modal showed.
 */

export interface EnrollmentSheetInput {
  orgCode: string;
  licenseKey: string;
  organizationName?: string | null;
  licenseLabel?: string | null;
  /** The rendered QR. Should be comfortably larger than its printed size. */
  qrCanvas: HTMLCanvasElement;
  /** Injected so the caller owns translation; see callers for the key names. */
  strings: EnrollmentSheetStrings;
}

export interface EnrollmentSheetStrings {
  title: string;
  intro: string;
  steps: string[];
  orgCodeLabel: string;
  licenseKeyLabel: string;
  manualHint: string;
  generatedOn: string;
}

const PAGE = { width: 210, height: 297 };
const MARGIN = 20;
const CONTENT_WIDTH = PAGE.width - MARGIN * 2;

const INK = { r: 15, g: 23, b: 42 };
const MUTED = { r: 100, g: 116, b: 139 };
const RULE = { r: 226, g: 232, b: 240 };
const PANEL = { r: 248, g: 250, b: 252 };

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    // A missing logo must not cost the user their sheet — fall back to a wordmark.
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

export async function buildEnrollmentSheet(
  input: EnrollmentSheetInput
): Promise<jsPDF> {
  const { orgCode, licenseKey, organizationName, licenseLabel, qrCanvas, strings } =
    input;

  const pdf = new jsPDF('p', 'mm', 'a4');
  let y = MARGIN;

  // ── Letterhead ────────────────────────────────────────────────────────────
  const logo = await loadImage(logoUrl);
  if (logo && logo.naturalWidth > 0) {
    const logoHeight = 12;
    // Derive width from the real aspect ratio so the mark is never stretched.
    const logoWidth = (logo.naturalWidth / logo.naturalHeight) * logoHeight;
    pdf.addImage(logo, 'PNG', MARGIN, y, logoWidth, logoHeight);
  } else {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(INK.r, INK.g, INK.b);
    pdf.text('DEEPECHO', MARGIN, y + 9);
  }
  y += 20;

  pdf.setDrawColor(RULE.r, RULE.g, RULE.b);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, y, PAGE.width - MARGIN, y);
  y += 12;

  // ── Title ─────────────────────────────────────────────────────────────────
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(INK.r, INK.g, INK.b);
  pdf.text(strings.title, MARGIN, y);
  y += 8;

  if (organizationName) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
    pdf.text(organizationName, MARGIN, y);
    y += 8;
  }

  // ── Intro ─────────────────────────────────────────────────────────────────
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10.5);
  pdf.setTextColor(INK.r, INK.g, INK.b);
  const introLines = pdf.splitTextToSize(strings.intro, CONTENT_WIDTH);
  pdf.text(introLines, MARGIN, y);
  y += introLines.length * 5.4 + 6;

  // ── QR ────────────────────────────────────────────────────────────────────
  const qrSize = 62;
  const qrX = (PAGE.width - qrSize) / 2;
  // Quiet zone: a QR flush against other ink is unreliable to scan.
  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(RULE.r, RULE.g, RULE.b);
  pdf.roundedRect(qrX - 5, y - 5, qrSize + 10, qrSize + 10, 3, 3, 'FD');
  pdf.addImage(qrCanvas.toDataURL('image/png'), 'PNG', qrX, y, qrSize, qrSize);
  y += qrSize + 14;

  // ── Steps ─────────────────────────────────────────────────────────────────
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(INK.r, INK.g, INK.b);
  strings.steps.forEach((step, index) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${index + 1}.`, MARGIN, y);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10.5);
    const lines = pdf.splitTextToSize(step, CONTENT_WIDTH - 8);
    pdf.text(lines, MARGIN + 7, y);
    y += lines.length * 5.4 + 3;
    pdf.setFontSize(11);
  });
  y += 5;

  // ── Credentials panel ─────────────────────────────────────────────────────
  const panelHeight = 34;
  pdf.setFillColor(PANEL.r, PANEL.g, PANEL.b);
  pdf.setDrawColor(RULE.r, RULE.g, RULE.b);
  pdf.roundedRect(MARGIN, y, CONTENT_WIDTH, panelHeight, 2.5, 2.5, 'FD');

  let panelY = y + 9;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
  pdf.text(strings.orgCodeLabel.toUpperCase(), MARGIN + 6, panelY);
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(INK.r, INK.g, INK.b);
  pdf.text(orgCode, MARGIN + 6, panelY + 6);

  panelY += 15;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
  pdf.text(strings.licenseKeyLabel.toUpperCase(), MARGIN + 6, panelY);
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(INK.r, INK.g, INK.b);
  pdf.text(licenseKey, MARGIN + 6, panelY + 6);

  y += panelHeight + 6;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
  const hintLines = pdf.splitTextToSize(strings.manualHint, CONTENT_WIDTH);
  pdf.text(hintLines, MARGIN, y);

  // ── Footer ────────────────────────────────────────────────────────────────
  const footerY = PAGE.height - 14;
  pdf.setDrawColor(RULE.r, RULE.g, RULE.b);
  pdf.line(MARGIN, footerY - 6, PAGE.width - MARGIN, footerY - 6);
  pdf.setFontSize(8);
  pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
  pdf.text(strings.generatedOn, MARGIN, footerY);
  if (licenseLabel) {
    pdf.text(licenseLabel, PAGE.width - MARGIN, footerY, { align: 'right' });
  }

  return pdf;
}
